/**
 * Custom hook for managing bank connections
 * Provides CRUD operations for connections with React Query
 *
 * WP-FRONTEND-CONN-1: Added hooks for connection details, force refresh, and account visibility
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { connectionsApi } from '@/services/connections';
import type {
  Connection,
  ExchangeTokenResponse,
  ConnectionDetail,
  ForceRefreshResult,
  SyncedAccountDetail,
  AccountVisibilityUpdate,
} from '@/types';

/**
 * Query key factory for connections
 */
export const connectionKeys = {
  all: ['connections'] as const,
  lists: () => [...connectionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...connectionKeys.lists(), filters] as const,
  details: () => [...connectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...connectionKeys.details(), id] as const,
  // WP-FE-7: Link token key for React Query caching (prevents duplicate API calls)
  // WP-CONN-1: No products in cache key - backend now uses fixed products
  linkTokens: () => [...connectionKeys.all, 'linkToken'] as const,
  linkToken: (connectionId: string | null) =>
    [...connectionKeys.linkTokens(), connectionId ?? 'new'] as const,
};

/**
 * Hook to fetch all connections
 */
export function useConnections() {
  return useQuery({
    queryKey: connectionKeys.lists(),
    queryFn: () => connectionsApi.getAll(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch a single connection
 */
export function useConnection(connectionId: string | undefined) {
  return useQuery({
    queryKey: connectionKeys.detail(connectionId || ''),
    queryFn: () => connectionsApi.getById(connectionId!),
    enabled: !!connectionId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to delete a connection
 */
export function useDeleteConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => connectionsApi.delete(connectionId),
    onSuccess: (_data, connectionId) => {
      // Remove from cache
      queryClient.setQueryData<Connection[]>(connectionKeys.lists(), (old) =>
        old?.filter((c) => c.id !== connectionId)
      );
      // Invalidate to refetch
      queryClient.invalidateQueries({ queryKey: connectionKeys.all });
    },
  });
}

/**
 * Hook to sync a connection
 */
export function useSyncConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => connectionsApi.forceSync(connectionId),
    onSuccess: (_data, connectionId) => {
      // Invalidate the specific connection and list
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(connectionId) });
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
  });
}

/**
 * Hook to handle successful connection creation
 * Used after Plaid Link completes successfully
 */
export function useOnConnectionCreated() {
  const queryClient = useQueryClient();

  return (response: ExchangeTokenResponse) => {
    // Invalidate connections list to refetch
    queryClient.invalidateQueries({ queryKey: connectionKeys.all });
    return response;
  };
}

// ============================================================================
// Connection Details & Maintenance (WP-FRONTEND-CONN-1)
// ============================================================================

/**
 * Hook to fetch detailed connection info including sync history and accounts.
 * Used by ConnectionDetailsModal for comprehensive view.
 *
 * @param connectionId - UUID of the connection (undefined to disable)
 */
export function useConnectionDetails(connectionId: string | undefined) {
  return useQuery({
    queryKey: connectionKeys.detail(connectionId || ''),
    queryFn: () => connectionsApi.getConnectionDetails(connectionId!),
    enabled: !!connectionId,
    staleTime: 1000 * 60 * 2, // 2 minutes - shorter for diagnostic data
  });
}

/**
 * Hook to force refresh a connection.
 * Clears sync cursor and re-fetches all data from Plaid.
 * Shows toast notifications for success/error.
 *
 * @returns Mutation object with mutate, isPending, etc.
 */
export function useForceRefresh() {
  const queryClient = useQueryClient();

  return useMutation<ForceRefreshResult, Error, string>({
    mutationFn: (connectionId: string) => connectionsApi.forceRefresh(connectionId),
    onSuccess: (data, connectionId) => {
      // Show success toast with sync statistics
      const txnCount = data.transactionsSynced;
      const acctCount = data.accountsSynced;
      toast.success('Connection refreshed', {
        description: `Synced ${acctCount} accounts and ${txnCount} transactions`,
      });
      // Invalidate all connection queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(connectionId) });
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
      // Also invalidate accounts and transactions queries since data changed
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (error) => {
      // Handle specific error cases
      const message = error.message || 'Failed to refresh connection';
      if (message.includes('409') || message.includes('in progress')) {
        toast.error('Sync already in progress', {
          description: 'Please wait for the current sync to complete',
        });
      } else if (message.includes('429') || message.includes('rate limit')) {
        toast.error('Rate limit exceeded', {
          description: 'Please wait a minute before trying again',
        });
      } else {
        toast.error('Refresh failed', {
          description: message,
        });
      }
    },
  });
}

/**
 * Hook to update account visibility (hide/show).
 * Implements optimistic updates for instant UI feedback.
 *
 * @returns Mutation object with mutate, isPending, etc.
 */
export function useUpdateAccountVisibility() {
  const queryClient = useQueryClient();

  return useMutation<
    SyncedAccountDetail,
    Error,
    { connectionId: string; accountId: string; data: AccountVisibilityUpdate },
    { previousDetail: ConnectionDetail | undefined }
  >({
    mutationFn: ({ connectionId, accountId, data }) =>
      connectionsApi.updateAccountVisibility(connectionId, accountId, data),
    // Optimistic update for instant UI feedback
    onMutate: async ({ connectionId, accountId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: connectionKeys.detail(connectionId) });

      // Snapshot previous value
      const previousDetail = queryClient.getQueryData<ConnectionDetail>(
        connectionKeys.detail(connectionId)
      );

      // Optimistically update the cache
      if (previousDetail) {
        queryClient.setQueryData<ConnectionDetail>(
          connectionKeys.detail(connectionId),
          {
            ...previousDetail,
            accounts: previousDetail.accounts.map((account: SyncedAccountDetail) =>
              account.id === accountId
                ? { ...account, isHidden: data.isHidden }
                : account
            ),
          }
        );
      }

      // Return context for rollback
      return { previousDetail };
    },
    onSuccess: (_data, { data }) => {
      // Show success toast
      const action = data.isHidden ? 'hidden' : 'visible';
      toast.success(`Account ${action}`, {
        description: data.isHidden
          ? 'Account will be excluded from reports'
          : 'Account will be included in reports',
      });
      // Invalidate accounts query since visibility affects aggregations
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error, { connectionId }, context) => {
      // Rollback on error
      if (context?.previousDetail) {
        queryClient.setQueryData(
          connectionKeys.detail(connectionId),
          context.previousDetail
        );
      }
      toast.error('Failed to update account', {
        description: error.message || 'Please try again',
      });
    },
    onSettled: (_data, _error, { connectionId }) => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(connectionId) });
    },
  });
}

export default {
  useConnections,
  useConnection,
  useDeleteConnection,
  useSyncConnection,
  useOnConnectionCreated,
  useConnectionDetails,
  useForceRefresh,
  useUpdateAccountVisibility,
  connectionKeys,
};
